import type { ScheduleSettings, ScheduleType } from '@repo/api-codegen';

import cronstrue from 'cronstrue';
import { debounce, isNumber } from 'lodash-es';
import { makeAutoObservable, reaction, runInAction } from 'mobx';
import { DAYS_OF_WEEK, HOURS_OF_DAY } from './constants';
import { isHourlyOrLessFrequent } from './utils';

export class ScheduleBlockStore {
	scheduleSettings: ScheduleSettings;
	invalidCronExpression: boolean;
	humanReadableSchedule?: string;
	tooFrequentCronExpression: boolean;
	cronExpression: string;
	debouncedCronExpression: string;

	constructor(initialScheduleSettings: ScheduleSettings) {
		makeAutoObservable(this, {}, { autoBind: true });

		this.scheduleSettings = initialScheduleSettings;
		this.invalidCronExpression = false;
		this.tooFrequentCronExpression = false;
		this.cronExpression = initialScheduleSettings.cron_expression ?? '';
		this.debouncedCronExpression = this.cronExpression;
		this.setHumanReadableSchedule();

		this.setDebouncedCronExpression = debounce((cronExpression: string) => {
			runInAction(() => {
				this.debouncedCronExpression = cronExpression;
			});
		}, 300);

		reaction(
			() => this.debouncedCronExpression,
			() => {
				this.setCronExpressionInScheduleSettings();
			}
		);
	}

	reset() {
		this.humanReadableSchedule = undefined;
		this.invalidCronExpression = false;
		this.tooFrequentCronExpression = false;
	}

	resetCronState() {
		this.invalidCronExpression = false;
		this.tooFrequentCronExpression = false;
	}

	setScheduleType(scheduleType: ScheduleType) {
		this.scheduleSettings = {
			...this.scheduleSettings,
			schedule_type: scheduleType,
		};
		this.setHumanReadableSchedule();
	}

	setDebouncedCronExpression: (cronExpression: string) => void;

	setCronExpression(cronExpression: string) {
		this.cronExpression = cronExpression;
		this.setDebouncedCronExpression(cronExpression);
	}

	setCronExpressionInScheduleSettings() {
		this.scheduleSettings = {
			...this.scheduleSettings,
			cron_expression: this.cronExpression,
		};
		this.setHumanReadableSchedule();
	}

	setDayOfWeek(dayOfWeek: number) {
		this.scheduleSettings = {
			...this.scheduleSettings,
			day_of_week: dayOfWeek,
		};
		this.setHumanReadableSchedule();
	}

	setHourOfDay(hourOfDay: number) {
		this.scheduleSettings = {
			...this.scheduleSettings,
			hour_of_day: hourOfDay,
		};
		this.setHumanReadableSchedule();
	}

	setHumanReadableSchedule() {
		const scheduleType = this.scheduleSettings.schedule_type;
		const hourOfDay = this.scheduleSettings.hour_of_day;
		const dayOfWeek = this.scheduleSettings.day_of_week;
		const { cronExpression } = this;

		if (scheduleType === 'hourly') {
			this.humanReadableSchedule = 'Automation will run every hour';
			this.resetCronState();
		} else if (scheduleType === 'daily') {
			this.humanReadableSchedule = `Automation will run every day at ${HOURS_OF_DAY[hourOfDay ?? 0].label}`;
			this.resetCronState();
		} else if (
			scheduleType === 'weekly' &&
			isNumber(dayOfWeek) &&
			isNumber(hourOfDay)
		) {
			this.humanReadableSchedule = `Automation will run every ${DAYS_OF_WEEK[dayOfWeek].label} at ${HOURS_OF_DAY[hourOfDay].label}`;
			this.resetCronState();
		} else if (scheduleType === 'cron' && cronExpression) {
			try {
				const cronString = cronstrue.toString(cronExpression);
				const formattedCronString =
					cronString.charAt(0).toLowerCase() + cronString.slice(1);

				if (!isHourlyOrLessFrequent(cronExpression)) {
					this.tooFrequentCronExpression = true;
					this.invalidCronExpression = false;
					this.humanReadableSchedule = undefined;
				} else {
					this.invalidCronExpression = false;
					this.tooFrequentCronExpression = false;
					this.humanReadableSchedule = `Automation will run ${formattedCronString}`;
				}
			} catch {
				this.invalidCronExpression = true;
				this.tooFrequentCronExpression = false;
				this.humanReadableSchedule = undefined;
			}
		} else {
			this.reset();
		}
	}
}
